import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Megaphone, Users, Kanban, Brain, Mail, UserCog, Search, BarChart3 } from 'lucide-react';
import { useTools, categories } from '../../hooks/useData';

const iconMap = {
  Megaphone: Megaphone,
  Users: Users,
  Kanban: Kanban,
  Brain: Brain,
  Mail: Mail,
  UserCog: UserCog,
  Search: Search,
  BarChart3: BarChart3,
};

const Categories = () => {
  const { tools } = useTools();

  // Calculate actual tool count for each category
  const getCategoryToolCount = (categorySlug) => {
    return tools.filter(t => t.categorySlug === categorySlug).length;
  };

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Browse by Category
            </h2>
            <p className="text-lg text-gray-600 max-w-xl">
              Explore our curated collection of software tools organized by business needs
            </p>
          </div>
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium group"
          >
            View all categories
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((category, index) => {
            const IconComponent = iconMap[category.icon] || Megaphone;
            const actualToolCount = getCategoryToolCount(category.slug);
            return (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className="group relative bg-white rounded-2xl border border-gray-100 p-6 hover:border-gray-200 hover:shadow-xl hover:shadow-gray-100/50 transition-all duration-300"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {/* Icon */}
                <div
                  className={`w-14 h-14 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                >
                  <IconComponent className="w-7 h-7 text-white" />
                </div>

                {/* Content */}
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                  {category.name}
                </h3>
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                  {category.description}
                </p>

                {/* Tool Count */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">
                    {actualToolCount} tools
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
                </div>

                {/* Hover Gradient */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/5 to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Categories;
